import { useEffect, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import Trans from 'next-translate/Trans';
import { Button, Modal, RadioGroup, type RadioChangeEvent } from '@arkestro/arkestro-design-system';
import { ManualSpendSummary } from '@components/bid_request/details/LineItemsTable/SmartBaselineBenchmarkRenderer/nsbManualSpend';
import { displayCurrency } from '@utils/currency';
import styles from './NsbNoBaselineWarningModal.module.scss';

// Optional reasons the buyer can record when proceeding with manual-entry baselines (council UX).
// `value` is the stable, backend-facing identifier; the display label is translated separately.
const REASONS = [
  { labelKey: 'unclear_specs', value: 'Unclear Specs' },
  { labelKey: 'item_basis_not_in_po_data', value: 'Item Basis not in PO Data' },
  { labelKey: 'new_item', value: 'New Item' },
  { labelKey: 'no_erroneous_baselines', value: 'No/Erroneous Baselines' },
];

interface NsbNoBaselineWarningModalProps {
  currency: string;
  onCancel: () => void;
  onProceed: (reason?: string) => void;
  // Non-null → the modal is open and carries the spend summary to display.
  summary: ManualSpendSummary | null;
}

// Pre-submit warning shown on Save when any line item uses a manual-entry baseline: how much spend is
// excluded from baseline-driven savings analysis, plus an optional reason. Proceed continues the save.
export const NsbNoBaselineWarningModal = ({ currency, onCancel, onProceed, summary }: NsbNoBaselineWarningModalProps) => {
  const { t } = useTranslation('common');
  const [reason, setReason] = useState<string | undefined>(undefined);
  const open = summary !== null;

  // Reset the (optional) reason each time the modal opens.
  useEffect(() => { if (open) setReason(undefined); }, [open]);

  return (
    <Modal
      centered
      footer={[
        <Button key='cancel' onClick={onCancel}>{t('general.cancel')}</Button>,
        <Button key='proceed' onClick={() => onProceed(reason)} type='primary'>{t('tlc_smart_baseline.no_baseline_warning_modal.proceed')}</Button>,
      ]}
      onCancel={onCancel}
      open={open}
      title={t('tlc_smart_baseline.no_baseline_warning_modal.title')}
      width={520}
    >
      {summary && (
        <>
          <p className={styles.body}>
            <Trans
              components={{ amount: <span className={styles.amount} /> }}
              i18nKey={`common:tlc_smart_baseline.no_baseline_warning_modal.body.${summary.manualCount === 1 ? 'singular' : 'plural'}`}
              values={{
                manualCount: summary.manualCount,
                manualPct: summary.manualPct,
                spend: displayCurrency(summary.manualSpend, currency, 0, 0),
              }}
            />
          </p>
          <div className={styles.reasonLabel}>{t('tlc_smart_baseline.no_baseline_warning_modal.reason_label')}</div>
          <RadioGroup
            className={styles.reasons}
            onChange={(e: RadioChangeEvent) => setReason(e.target.value)}
            options={REASONS.map(({ labelKey, value }) => ({
              label: t(`tlc_smart_baseline.no_baseline_warning_modal.reasons.${labelKey}`),
              value,
            }))}
            value={reason}
          />
        </>
      )}
    </Modal>
  );
};
